/**
 * Locale internals that survive time-zone spoofing.
 *
 * Someone masking their location typically changes the obvious dials — the time
 * zone, or the primary language tag. Three settings are usually left alone
 * because they are buried in OS regional preferences rather than in a browser
 * menu, and because Tor Browser normalises the zone but not these:
 *
 *   1. calendar         — a Persian or Buddhist calendar is regionally specific.
 *   2. numberingSystem  — Persian, Bengali, or Devanagari digits likewise.
 *   3. navigator.languages — the FULL preference list. Setting the primary to
 *      en-US commonly leaves a secondary entry like fa or ar-SY in place.
 *
 * These are population-level regional signals, never identification, and they
 * are wrong for people who simply prefer another calendar or keyboard.
 */

export interface LocaleSignals {
  /** Resolved calendar, e.g. 'gregory' | 'persian' | 'buddhist'. */
  calendar: string | null
  /** Resolved digit system, e.g. 'latn' | 'arabext' | 'beng'. */
  numberingSystem: string | null
  /**
   * Full accepted-language list, comma-joined and lowercased. Kept in preference
   * order because a secondary entry is the informative part.
   */
  languages: string | null
}

export function readLocaleSignals(): LocaleSignals {
  let calendar: string | null = null
  let numberingSystem: string | null = null

  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions()
    calendar = resolved.calendar ?? null
    numberingSystem = resolved.numberingSystem ?? null
  } catch {
    /* Intl is unavailable in some embedded webviews */
  }

  let languages: string | null = null
  try {
    const list = navigator.languages
    if (Array.isArray(list) && list.length > 0) {
      // Capped so an unusual client cannot write an unbounded string.
      languages = list.slice(0, 8).join(',').toLowerCase()
    } else if (navigator.language) {
      languages = navigator.language.toLowerCase()
    }
  } catch {
    /* ignore */
  }

  return { calendar, numberingSystem, languages }
}

/**
 * True when the visitor's locale internals disagree with their primary language
 * tag — the shape left behind when the surface settings were changed but the
 * regional preferences underneath were not.
 */
export function hasLocaleTension(signals: LocaleSignals, primaryLanguage: string): boolean {
  const primary = primaryLanguage.toLowerCase()

  // A non-Gregorian calendar alongside an English interface.
  if (signals.calendar && signals.calendar !== 'gregory' && primary.startsWith('en')) return true

  // Non-Latin digits alongside an English interface.
  if (signals.numberingSystem && signals.numberingSystem !== 'latn' && primary.startsWith('en')) {
    return true
  }

  // A secondary accepted language from a different language family than the
  // primary: e.g. 'en-us,fa' — the fa entry is the residue.
  if (signals.languages) {
    const families = signals.languages
      .split(',')
      .map(tag => tag.trim().split('-')[0])
      .filter(Boolean)
    const primaryFamily = primary.split('-')[0]
    if (families.some(family => family !== primaryFamily)) return true
  }

  return false
}
